import { G } from '@mobily/ts-belt';
import cookie from 'browser-cookies';
import dayjs from 'dayjs';
import { z } from 'zod';
import type { FlowContext } from './flow-machine';
import { flows } from './flows';
import type { FixScheduleInput } from '../../../../types/definitions/vendor/graphql-schema.d';
export const FIX_SCHEDULE_DATA_COOKIE_NAME = 'cob_lff_cadence';
const cadenceSchema = z.object({
  clientWantsFixOn: z.string().optional(),
  firstAvailableFixDate: z.string().optional()
});
export const persistStepData = (data: Partial<FixScheduleInput> = {}) => {
  cookie.set(FIX_SCHEDULE_DATA_COOKIE_NAME, JSON.stringify({
    clientWantsFixOn: data?.clientWantsFixOn,
    firstAvailableFixDate: data?.firstAvailableFixDate
  }), {
    expires: dayjs().add(1, 'hour').toDate()
  });
};
export const clearPersistedStepData = () => {
  cookie.erase(FIX_SCHEDULE_DATA_COOKIE_NAME);
};
export const getPersistedStepData = (requestCookies: Partial<Record<string, string>> = {}) => {
  try {
    return cadenceSchema.parse(JSON.parse(decodeURIComponent(requestCookies[FIX_SCHEDULE_DATA_COOKIE_NAME] ?? '{}')));
  } catch (e) {
    return {};
  }
};
export const getFlowSteps = (context: FlowContext) => flows[context.flow].filter(step => {
  const businessLineVisible = !context.businessLine || step.restrictedBusinessLines === undefined || step.restrictedBusinessLines?.includes(context.businessLine) === true;
  const stepVisible = step.isVisible?.(context) ?? true;
  return businessLineVisible && stepVisible;
});
export const getStepIndex = (context: FlowContext, step?: string) => getFlowSteps(context).findIndex(f => f.step === step);
export const getFirstAvailableStepIndex = (context: FlowContext) => getFlowSteps(context).length > 0 ? 0 : -1;

/**
 * Builds the router path from a flow and identifier
 * @param flow flowId
 * @param identifier of the step, as an index of the flow or a stepId name
 * @param fallback the path returned if identifier does not exist in flow
 * @returns the path for the flow and step identifier
 */
export const getStepPath = ({
  identifier,
  fallback = '/',
  ...context
}: FlowContext & {
  identifier: number | string;
  fallback?: string;
}) => {
  const flowSteps = getFlowSteps(context);
  const step = G.isString(identifier) ? flowSteps.find(f => f.step === identifier) : flowSteps[identifier];
  if (G.isUndefined(step)) {
    return fallback;
  }
  return `/onboarding/get-your-fix/${context.flow}/${step.step}`;
};
export const getSteps = ({
  currentStepIndex,
  ...context
}: FlowContext) => ({
  currentStepIndex,
  nextStepPath: getStepPath({
    ...context,
    currentStepIndex,
    identifier: currentStepIndex + 1
  }),
  previousStepPath: getStepPath({
    ...context,
    currentStepIndex,
    identifier: currentStepIndex - 1,
    fallback: '/onboarding/profile-note'
  })
});